﻿import React, { useEffect, useState, useRef } from "react";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import Styles from "./TermsConditionModal.module.css";

import { ReactComponent as EditIcon } from "../../../../assets/images/editIcon.svg";
import { ReactComponent as SquareIcon } from "../../../../assets/images/view.svg";
import FinalEditModal from "../FinalEditModal/FinalEditModal";
import { acceptBidTerms } from "../../../../Services/ApiCalling/banking";
import Loader from "../../../../commons/Loader/Loader";
import ViewInjectionModal from "../../Banking/ViewInjectionModal/ViewInjectionModal";
import ViewDrawalModal from "../../Banking/ViewDrawalModal/ViewDrawalModal";
import TermsTable from "./TermsTable/TermsTable";
import ConnectionAcceptModal from "../../../../commons/PopupModal/BankingPopup/ConnectionAcceptModal/ConnectionAcceptModal";
import {
  fetchMatchmakingResults,
  fetchAllBankingListings,
  fetchLedgerBanking,
  clearMatchmakingData,
} from "../../../../Redux/Slices/bankingSlice";

const TermsConditionModal = ({
  open,
  handleClose,
  connectionId,
  onAccept,
  termsData,
  onDecline,
  activeListingId,
  currentUserListingIds,
  activeData,
  counterpartyId,
  activeCounterparty,
  viewOnly,
  isApprovalMode,
  showAlreadyAcceptedMessage,
}) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.persistedReducer);
  const currentUserCompanyName = userData?.user?.company?.displayName;
  const userId = userData?.user?.user?.userId;

  const [forceRefreshKey, setForceRefreshKey] = useState(0);
  const [lastNotificationId, setLastNotificationId] = useState(null);

  const matchmakingData = useSelector(
    (state) => state?.bankingSlice?.matchmakingData || {}
  );
  const bankingData = useSelector(
    (state) => state?.bankingSlice?.matchmakingResults?.data || []
  );
  const lastMatchmakingUpdate = useSelector(
    (state) => state?.bankingSlice?.lastMatchmakingUpdate
  );
  const bankingForceRefreshKey = useSelector(
    (state) => state?.bankingSlice?.forceRefreshKey
  );

  const notifications = useSelector(
    (state) => state?.notificationSlice?.notifications || []
  );

  const isOngoingBankingFormat = !!activeData?.bankingListing;

  const freshActiveData = React.useMemo(() => {
    if (
      activeListingId &&
      matchmakingData?.selfListing?.id === activeListingId
    ) {
      return matchmakingData.selfListing;
    }

    if (activeListingId && matchmakingData?.matches?.length > 0) {
      const matchingListing = matchmakingData.matches.find(
        (match) => match.id === activeListingId
      );
      if (matchingListing) {
        return matchingListing;
      }
    }

    if (!activeListingId || !bankingData.length) return activeData;

    const currentListing = bankingData.find(
      (listing) => listing.id === activeListingId
    );
    if (currentListing) {
      return currentListing;
    }

    return activeData;
  }, [
    activeData,
    activeListingId,
    bankingData,
    matchmakingData,
    forceRefreshKey,
    bankingForceRefreshKey,
  ]);

  const selfListingData = isOngoingBankingFormat
    ? freshActiveData.bankingListing
    : freshActiveData?.selfListing || freshActiveData;

  const counterpartyData = isOngoingBankingFormat
    ? activeCounterparty
    : freshActiveData?.againstBankingListing || {};

  const finalizedValues = React.useMemo(() => {
    let values = {};

    if (isOngoingBankingFormat) {
      values = freshActiveData.finalizedConnectionValues || {};
    } else {
      if (freshActiveData?.connectionDetails?.[0]?.finalizedConnectionValues) {
        values = freshActiveData.connectionDetails[0].finalizedConnectionValues;
      } else if (freshActiveData?.finalizedConnectionValues) {
        values = freshActiveData.finalizedConnectionValues;
      }
    }

    return values;
  }, [freshActiveData, isOngoingBankingFormat]);

  const contractId =
    activeData?.counterPartyListings?.[0]?.connectionUniqueId ||
    activeData?.connectionUniqueId ||
    activeCounterparty?.connectionUniqueId ||
    freshActiveData?.connectionDetails?.[0]?.connectionUniqueId ||
    null;

  const showButtons =
    selfListingData?.status !== "TERMS_ACCEPTED" &&
    selfListingData?.status !== "COMPLETED" &&
    activeCounterparty.status !== "TERMS_ACCEPTED" &&
    activeCounterparty.status !== "COMPLETED";

  useEffect(() => {
    if (open && activeListingId && (isApprovalMode || showButtons)) {
      dispatch(
        fetchMatchmakingResults({
          id: activeListingId,
          statusFilter: null,
        })
      );
    }
  }, [open, activeListingId, isApprovalMode, showButtons, dispatch]);

  useEffect(() => {
    if (open && activeListingId) {
      const cacheBuster = Date.now();

      dispatch(
        fetchMatchmakingResults({
          id: activeListingId,
          statusFilter: null,
          _cacheBust: cacheBuster,
          _hardRefresh: true,
          _modalOpened: true,
        })
      );

      dispatch(
        fetchAllBankingListings({
          _cacheBust: cacheBuster,
          _hardRefresh: true,
        })
      );

      dispatch(
        fetchLedgerBanking({
          currentPage: 0,
          size: 20,
          _cacheBust: cacheBuster,
          _hardRefresh: true,
        })
      );
    }
  }, [open, activeListingId, dispatch]);

  useEffect(() => {
    if (open && activeListingId && notifications.length > 0) {
      const latestNotification = notifications[notifications.length - 1];
      const notificationId =
        latestNotification?.data?.key || latestNotification?.key;

      if (
        notificationId &&
        notificationId.startsWith("BANKING") &&
        notificationId !== lastNotificationId
      ) {
        setLastNotificationId(notificationId);

        const refreshPromise = dispatch(
          fetchMatchmakingResults({
            id: activeListingId,
            statusFilter: null,
            _cacheBust: Date.now(),
          })
        );

        refreshPromise.then(() => {
          setForceRefreshKey((prev) => prev + 1);
        });

        dispatch(fetchAllBankingListings());
      }
    }
  }, [
    notifications,
    open,
    activeListingId,
    dispatch,
    lastNotificationId,
    forceRefreshKey,
  ]);

  useEffect(() => {
    if (open && lastMatchmakingUpdate) {
      setForceRefreshKey((prev) => prev + 1);
    }
  }, [lastMatchmakingUpdate, open]);

  useEffect(() => {
    if (open && bankingForceRefreshKey > 0) {
      setForceRefreshKey((prev) => prev + 1);
    }
  }, [bankingForceRefreshKey, open]);

  useEffect(() => {
    if (open && activeListingId) {
      let hasChanges = false;
      let changeSource = "";

      if (matchmakingData?.selfListing?.id === activeListingId) {
        hasChanges = true;
        changeSource = "selfListing";
      }

      if (matchmakingData?.matches?.length > 0) {
        const matchingListing = matchmakingData.matches.find(
          (match) => match.id === activeListingId
        );
        if (matchingListing) {
          hasChanges = true;
          changeSource = changeSource ? `${changeSource}+matches` : "matches";
        }
      }

      if (hasChanges) {
        setForceRefreshKey((prev) => prev + 1);
      }
    }
  }, [
    open,
    activeListingId,
    matchmakingData,
    matchmakingData?.selfListing,
    matchmakingData?.matches,
  ]);

  useEffect(() => {
    if (open && activeListingId && notifications.length > 0) {
      const latestNotification = notifications[notifications.length - 1];
      if (
        latestNotification?.data?.key &&
        latestNotification.data.key.startsWith("BANKING")
      ) {
        const hardRefreshCacheBuster = Date.now();

        dispatch(
          fetchMatchmakingResults({
            id: activeListingId,
            statusFilter: null,
            _cacheBust: hardRefreshCacheBuster,
            _hardRefresh: true,
            _modalRefresh: true,
          })
        );

        dispatch(fetchAllBankingListings());
        dispatch(
          fetchLedgerBanking({
            currentPage: 0,
            size: 20,
            _cacheBust: hardRefreshCacheBuster,
          })
        );
      }
    }
  }, [notifications, open, activeListingId, dispatch]);

  const [editFinalModal, setEditFinalModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMore, setViewMore] = useState(false);
  const [viewInjection, setViewInjection] = useState({
    open: false,
    data: [],
  });
  const [isFinalized, setIsFinalized] = useState(false);

  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [popupAction, setPopupAction] = useState("");

  const openConfirmPopup = (action) => {
    setPopupAction(action);
    setConfirmPopupOpen(true);
  };

  const hasAnyCounterpartyAcceptedTerms = () => {
    if (
      !activeData?.counterPartyListings ||
      !Array.isArray(activeData.counterPartyListings)
    ) {
      return false;
    }

    return activeData.counterPartyListings.some(
      (counterparty) =>
        counterparty.status === "TERMS_ACCEPTED" ||
        counterparty.status === "COMPLETED"
    );
  };
  const [viewDrawal, setViewDrawal] = useState({
    open: false,
    data: [],
  });

  const hasUserEdits = useRef(false);

  const [finalModalEditData, setFinalModalEditData] = useState({
    injectionRows: [
      {
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        quantity: "",
      },
    ],
    drawalRows: [
      {
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        quantity: "",
      },
    ],
    settlementRate: "",
    bankingFactor: "",
    expiryDate: "",
    expiryTime: "",
    termsExpiryDate: "",
    termsExpiryTime: "",
    activeType: "",
  });

  const [finalTermsData, setFinalTermsData] = useState(null);
  const [initialTermsData, setInitialTermsData] = useState(null);

  const toggleViewMore = () => setViewMore((prev) => !prev);

  const formatDate = (isoDateStr) => {
    if (!isoDateStr) return "";
    const date = new Date(isoDateStr);
    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const monthShortNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthShortNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const formatTimeToHHMM = (time) => {
    if (!time) return "";
    const [hoursStr, minutesStr] = time.split(":");
    if (!hoursStr || !minutesStr) return "";

    const hours = hoursStr.padStart(2, "0");
    const minutes = minutesStr.padStart(2, "0");

    const h = Number(hours);
    const m = Number(minutes);
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return "";

    if (hours === "23" && minutes === "59") {
      return "24:00";
    }

    return `${hours}:${minutes}`;
  };

  const displayTime = (time) => {
    if (!time) return "—";
    if (time === "23:59") return "24:00";
    return time;
  };

  const convertDisplayTimeToBackend = (displayTime) => {
    if (!displayTime) return "";
    if (displayTime === "24:00") return "23:59";
    return displayTime;
  };

  const convertBackendTimeToDisplay = (backendTime) => {
    if (!backendTime) return "";
    if (backendTime === "23:59") return "24:00";
    return backendTime;
  };

  useEffect(() => {
    if (open && selfListingData && !hasUserEdits.current) {
      const finalizedData =
        activeData?.finalizedConnectionValues ||
        activeCounterparty?.finalizedConnectionValues;

      const hasFinalized = !!(
        finalizedData &&
        (finalizedData.slotInjects?.length > 0 ||
          finalizedData.slotDrawls?.length > 0 ||
          finalizedData.bankingFactor != null ||
          finalizedData.settlementRate != null)
      );

      const originalSlotInjects = selfListingData?.bankingSlotInjects || [];
      const originalSlotDrawls = selfListingData?.bankingSlotDrawls || [];

      let injectionRows = [];
      let drawalRows = [];

      if (hasFinalized) {
        injectionRows = (finalizedData.slotInjects || []).map(
          (finalizedSlot, index) => {
            const originalSlot = originalSlotInjects[index] || {};
            return {
              id: originalSlot.id || finalizedSlot.id,
              dateStartAt: formatDate(finalizedSlot.dateStartAt),
              dateEndAt: formatDate(finalizedSlot.dateEndAt),
              slotStartAt:
                convertBackendTimeToDisplay(finalizedSlot.slotStartAt) || "",
              slotEndAt:
                convertBackendTimeToDisplay(finalizedSlot.slotEndAt) || "",
              requiredQty: finalizedSlot.requiredQty ?? "",
            };
          }
        );

        drawalRows = (finalizedData.slotDrawls || []).map(
          (finalizedSlot, index) => {
            const originalSlot = originalSlotDrawls[index] || {};
            return {
              id: originalSlot.id || finalizedSlot.id,
              dateStartAt: formatDate(finalizedSlot.dateStartAt),
              dateEndAt: formatDate(finalizedSlot.dateEndAt),
              slotStartAt:
                convertBackendTimeToDisplay(finalizedSlot.slotStartAt) || "",
              slotEndAt:
                convertBackendTimeToDisplay(finalizedSlot.slotEndAt) || "",
              requiredQty: finalizedSlot.requiredQty ?? "",
            };
          }
        );
      } else {
        injectionRows = originalSlotInjects.map((originalSlot) => ({
          id: originalSlot.id,
          dateStartAt: formatDate(originalSlot.dateStartAt),
          dateEndAt: formatDate(originalSlot.dateEndAt),
          slotStartAt:
            convertBackendTimeToDisplay(originalSlot.slotStartAt) || "",
          slotEndAt: convertBackendTimeToDisplay(originalSlot.slotEndAt) || "",
          requiredQty: originalSlot.requiredQty ?? "",
        }));

        drawalRows = originalSlotDrawls.map((originalSlot) => ({
          id: originalSlot.id,
          dateStartAt: formatDate(originalSlot.dateStartAt),
          dateEndAt: formatDate(originalSlot.dateEndAt),
          slotStartAt:
            convertBackendTimeToDisplay(originalSlot.slotStartAt) || "",
          slotEndAt: convertBackendTimeToDisplay(originalSlot.slotEndAt) || "",
          requiredQty: originalSlot.requiredQty ?? "",
        }));
      }

      const bf = hasFinalized
        ? finalizedData?.bankingFactor
        : selfListingData?.bankingFactor;

      const sr = hasFinalized
        ? finalizedData?.settlementRate
        : selfListingData?.settlementRate;

      const gnaType = hasFinalized
        ? finalizedData?.gnaepleListing
        : selfListingData?.gnaepleListing;

      const companyName = hasFinalized ? finalizedData?.companyName : null;

      // Base terms (snapshot for comparison)
      const baseTerms = {
        slotInjects: injectionRows.length
          ? injectionRows
          : [
              {
                dateStartAt: "",
                dateEndAt: "",
                slotStartAt: "",
                slotEndAt: "",
                requiredQty: "",
              },
            ],
        slotDrawls: drawalRows.length
          ? drawalRows
          : [
              {
                dateStartAt: "",
                dateEndAt: "",
                slotStartAt: "",
                slotEndAt: "",
                requiredQty: "",
              },
            ],
        bankingFactor: bf ?? "",
        settlementRate: (sr || "").toString().replace("%", ""),
        activeType: gnaType || "",
        companyName: companyName || null,
      };

      // Used by edit modal
      setFinalModalEditData({
        slotInjects: baseTerms.slotInjects,
        slotDrawls: baseTerms.slotDrawls,
        bankingFactor: baseTerms.bankingFactor?.toString() || "",
        settlementRate: baseTerms.settlementRate,
        expiryDate: selfListingData?.expiryAt || "",
        expiryTime: "",
        termsExpiryDate: selfListingData?.termsExpiryAt || "",
        termsExpiryTime: "",
        activeType: baseTerms.activeType,
        companyName: baseTerms.companyName,
      });

      setFinalTermsData(baseTerms);

      setInitialTermsData(baseTerms);
    }
  }, [open, selfListingData, activeCounterparty, activeData]);

  useEffect(() => {
    if (!open) {
      hasUserEdits.current = false;
    }
  }, [open]);

  const prepareFinalPayload = (isAccepting) => {
    const ensureFormattedDate = (dateValue) => {
      if (!dateValue) return "";

      if (
        typeof dateValue === "string" &&
        /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(dateValue)
      ) {
        return dateValue;
      }

      return formatDate(dateValue);
    };

    const convertTimeForBackend = (time) => {
      if (!time) return "";

      if (time === "24:00") {
        return "23:59";
      }

      const formattedTime = formatTimeToHHMM(time);
      if (formattedTime === "24:00") {
        return "23:59";
      }

      return time;
    };

    const transformSlotRows = (rows) =>
      rows.map((row) => {
        const transformedRow = {
          dateStartAt: ensureFormattedDate(row.dateStartAt),
          dateEndAt: ensureFormattedDate(row.dateEndAt),
          slotStartAt: convertTimeForBackend(row.slotStartAt),
          slotEndAt: convertTimeForBackend(row.slotEndAt),
          requiredQty: parseFloat(row.requiredQty) || 0,
        };

        if (row.id) {
          transformedRow.id = row.id;
        }

        return transformedRow;
      });

    const payload = {
      isAccepting,
      connectionId:
        activeCounterparty?.connectionId ||
        activeCounterparty?.bidId ||
        activeCounterparty?.connectionDetails?.[0]?.id,
      bankingFactor: parseFloat(finalTermsData.bankingFactor) || 0,
      settlementRate: `${finalTermsData?.settlementRate || 0}`,
      slotInjects: transformSlotRows(finalModalEditData.slotInjects),
      slotDrawls: transformSlotRows(finalModalEditData.slotDrawls),
      gnaepleListing: finalModalEditData.activeType || "",
      companyName: finalTermsData?.companyName || `${currentUserCompanyName}`,
    };

    return payload;
  };

  const handleAcceptClick = async (status) => {
    setIsLoading(true);

    if (status === "ACCEPTED" || status === "FINALIZE") {
      if (!finalModalEditData) {
        setIsLoading(false);
        return;
      }

      const payload = prepareFinalPayload(true);

      try {
        await acceptBidTerms(payload);
        onAccept();
        dispatch(clearMatchmakingData());

        if (activeListingId) {
          const refreshCacheBuster = Date.now();

          await Promise.all([
            dispatch(
              fetchMatchmakingResults({
                id: activeListingId,
                statusFilter: null,
                _cacheBust: refreshCacheBuster,
                _hardRefresh: true,
                _termsActionCompleted: true,
              })
            ),
            dispatch(
              fetchAllBankingListings({
                _cacheBust: refreshCacheBuster,
                _hardRefresh: true,
                _termsActionCompleted: true,
              })
            ),
            dispatch(
              fetchLedgerBanking({
                currentPage: 0,
                size: 20,
                _cacheBust: refreshCacheBuster,
                _hardRefresh: true,
              })
            ),
          ]);
        }

        handleClose();
      } catch (err) {
        if (err?.response?.data?.message?.includes("TERMS_ACCEPTED")) {
          toast.error("Terms have already been accepted.");
        } else {
          toast.error("Failed to submit bid terms.");
        }
      } finally {
        setIsLoading(false);
      }
    } else if (status === "DECLINED") {
      const payload = prepareFinalPayload(false);

      try {
        await acceptBidTerms(payload);
        onDecline();
        toast.success("Terms have been declined.");
        dispatch(clearMatchmakingData());

        if (activeListingId) {
          const refreshCacheBuster = Date.now();

          await Promise.all([
            dispatch(
              fetchMatchmakingResults({
                id: activeListingId,
                statusFilter: null,
                _cacheBust: refreshCacheBuster,
                _hardRefresh: true,
                _termsActionCompleted: true,
              })
            ),
            dispatch(
              fetchAllBankingListings({
                _cacheBust: refreshCacheBuster,
                _hardRefresh: true,
                _termsActionCompleted: true,
              })
            ),
            dispatch(
              fetchLedgerBanking({
                currentPage: 0,
                size: 20,
                _cacheBust: refreshCacheBuster,
                _hardRefresh: true,
              })
            ),
          ]);
        }

        handleClose();
      } catch (err) {
        toast.error("Failed to decline bid terms.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEditFinalModal = () => {
    if (!selfListingData) return;

    if (!hasUserEdits.current) {
      const finalizedData =
        activeData?.finalizedConnectionValues ||
        activeCounterparty?.finalizedConnectionValues;

      let injectionRows = [];
      let drawalRows = [];

      if (finalizedData && finalizedData.slotInjects?.length > 0) {
        injectionRows = finalizedData.slotInjects.map(
          (finalizedSlot, index) => {
            const originalSlot =
              (selfListingData.bankingSlotInjects || [])[index] || {};
            return {
              id: originalSlot.id || finalizedSlot.id,
              dateStartAt: formatDate(finalizedSlot.dateStartAt),
              dateEndAt: formatDate(finalizedSlot.dateEndAt),
              slotStartAt:
                convertBackendTimeToDisplay(finalizedSlot.slotStartAt) || "",
              slotEndAt:
                convertBackendTimeToDisplay(finalizedSlot.slotEndAt) || "",
              requiredQty: finalizedSlot.requiredQty ?? "",
            };
          }
        );
      } else {
        injectionRows = (selfListingData.bankingSlotInjects || []).map(
          (slot) => ({
            id: slot.id,
            dateStartAt: formatDate(slot.dateStartAt),
            dateEndAt: formatDate(slot.dateEndAt),
            slotStartAt: convertBackendTimeToDisplay(slot.slotStartAt) || "",
            slotEndAt: convertBackendTimeToDisplay(slot.slotEndAt) || "",
            requiredQty: slot.requiredQty ?? "",
          })
        );
      }

      if (finalizedData && finalizedData.slotDrawls?.length > 0) {
        drawalRows = finalizedData.slotDrawls.map((finalizedSlot, index) => {
          const originalSlot =
            (selfListingData.bankingSlotDrawls || [])[index] || {};
          return {
            id: originalSlot.id || finalizedSlot.id,
            dateStartAt: formatDate(finalizedSlot.dateStartAt),
            dateEndAt: formatDate(finalizedSlot.dateEndAt),
            slotStartAt:
              convertBackendTimeToDisplay(finalizedSlot.slotStartAt) || "",
            slotEndAt:
              convertBackendTimeToDisplay(finalizedSlot.slotEndAt) || "",
            requiredQty: finalizedSlot.requiredQty ?? "",
          };
        });
      } else {
        drawalRows = (selfListingData.bankingSlotDrawls || []).map((slot) => ({
          id: slot.id,
          dateStartAt: formatDate(slot.dateStartAt),
          dateEndAt: formatDate(slot.dateEndAt),
          slotStartAt: convertBackendTimeToDisplay(slot.slotStartAt) || "",
          slotEndAt: convertBackendTimeToDisplay(slot.slotEndAt) || "",
          requiredQty: slot.requiredQty ?? "",
        }));
      }

      setFinalModalEditData({
        slotInjects:
          injectionRows.length > 0
            ? injectionRows
            : [
                {
                  dateStartAt: "",
                  dateEndAt: "",
                  slotStartAt: "",
                  slotEndAt: "",
                  requiredQty: "",
                },
              ],
        slotDrawls:
          drawalRows.length > 0
            ? drawalRows
            : [
                {
                  dateStartAt: "",
                  dateEndAt: "",
                  slotStartAt: "",
                  slotEndAt: "",
                  requiredQty: "",
                },
              ],
        bankingFactor:
          finalizedData?.bankingFactor?.toString() ||
          selfListingData.bankingFactor?.toString() ||
          "",
        settlementRate:
          (
            finalizedData?.settlementRate ||
            selfListingData.settlementRate ||
            ""
          ).replace("%", "") || "",
        expiryDate: selfListingData.expiryAt || "",
        termsExpiryDate: selfListingData.termsExpiryAt || "",
        expiryTime: "",
        termsExpiryTime: "",
        activeType:
          finalizedData?.gnaepleListing ||
          selfListingData?.gnaepleListing ||
          "",
      });
    }

    setEditFinalModal(true);
  };

  const handleCloseEditFinalModal = () => {
    if (initialTermsData) {
      setFinalModalEditData({
        slotInjects: JSON.parse(
          JSON.stringify(initialTermsData.slotInjects || [])
        ),
        slotDrawls: JSON.parse(
          JSON.stringify(initialTermsData.slotDrawls || [])
        ),
        bankingFactor: initialTermsData.bankingFactor?.toString() || "",
        settlementRate: initialTermsData.settlementRate || "",
        expiryDate: selfListingData?.expiryAt || "",
        expiryTime: "",
        termsExpiryDate: selfListingData?.termsExpiryAt || "",
        termsExpiryTime: "",
        activeType: initialTermsData.activeType || "",
        companyName: initialTermsData.companyName || null,
      });
      setFinalTermsData(initialTermsData);
    }
    setEditFinalModal(false);
  };

  const handleFinalSubmit = (updatedValues) => {
    const preserveIdsInRows = (updatedRows, originalRows) => {
      return updatedRows.map((updatedRow, index) => ({
        ...updatedRow,
        requiredQty: updatedRow.requiredQty
          ? Number(updatedRow.requiredQty)
          : updatedRow.requiredQty,
        ...(originalRows[index]?.id && { id: originalRows[index].id }),
      }));
    };

    const updatedFinalData = {
      ...finalModalEditData,
      ...updatedValues,

      slotInjects: updatedValues.slotInjects
        ? preserveIdsInRows(
            updatedValues.slotInjects,
            finalModalEditData.slotInjects
          )
        : finalModalEditData.slotInjects,
      slotDrawls: updatedValues.slotDrawls
        ? preserveIdsInRows(
            updatedValues.slotDrawls,
            finalModalEditData.slotDrawls
          )
        : finalModalEditData.slotDrawls,
      expiryDate: updatedValues.expiryDate,
      bankingFactor:
        Number(updatedValues.bankingFactor) ||
        updatedValues.bankingFactor ||
        "",
      settlementRate: updatedValues.settlementRate || "",
      activeType:
        updatedValues.activeType || selfListingData?.gnaepleListing || "",
      companyName:
        updatedValues.companyName || finalModalEditData.companyName || null,
    };

    setFinalModalEditData({ ...updatedFinalData });
    setFinalTermsData(updatedFinalData);
    setEditFinalModal(false);

    hasUserEdits.current = true;
  };

  const isValueModified = (originalValue, finalValue) => {
    if (finalValue === undefined || finalValue === null) return false;

    const normalizeValue = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === "string") {
        // Remove common symbols, units, and normalize hyphens/dashes
        return val
          .replace(/[%₹]|kWh|\/kWh|g|\s+|-/g, "")
          .trim()
          .toUpperCase();
      }
      return String(val);
    };

    const normalizedOriginal = normalizeValue(originalValue);
    const normalizedFinal = normalizeValue(finalValue);

    // For numeric comparisons
    const originalNum = parseFloat(normalizedOriginal);
    const finalNum = parseFloat(normalizedFinal);

    if (!isNaN(originalNum) && !isNaN(finalNum)) {
      return originalNum !== finalNum;
    }

    // For string comparisons (like "TGNA" vs "T-GNA")
    return normalizedOriginal !== normalizedFinal;
  };

  const renderTermData = (
    label,
    myValue,
    counterpartyValue,
    finalValue,
    myValueClass = "",
    counterpartyValueClass = ""
  ) => (
    <div className={Styles.termsTableElem} key={label}>
      <div className={`${Styles.categoryHead} ${Styles.nameLabel}`}>
        {label}
      </div>
      <div className={`${Styles.categoryHead} ${myValueClass}`}>
        {myValue || "—"}
      </div>
      <div className={`${Styles.categoryHead} ${counterpartyValueClass}`}>
        {counterpartyValue || "—"}
      </div>
      <div className={Styles.categoryHeadF}>{finalValue || "—"}</div>
    </div>
  );

  function formatDateTime(input) {
    if (!input) return "—";
    const date = new Date(input);
    const day = date.getDate().toString().padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    const hours = date.getHours().toString().padStart(2, "0");
    const mins = date.getMinutes().toString().padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${mins}`;
  }

  const counterpartyUserId = activeData?.counterPartyListings?.[0]?.userId;
  const termsInitiatorId =
    activeData?.counterPartyListings?.[0]?.termsInitiatorId;
  const connectionIsTermsInitiated =
    activeData?.counterPartyListings?.[0]?.connectionStatus ===
    "TERMS_INITIATED";

  const loggedInUserId = userId;
  const showApproveButton =
    connectionIsTermsInitiated &&
    termsInitiatorId &&
    loggedInUserId !== termsInitiatorId &&
    loggedInUserId !== counterpartyUserId;

  useEffect(() => {
    if (showApproveButton && activeListingId) {
      dispatch(
        fetchMatchmakingResults({
          id: activeListingId,
          statusFilter: null,
        })
      );
    }
  }, [showApproveButton, activeListingId, dispatch]);

  useEffect(() => {
    if (open && activeListingId && (activeData || activeCounterparty)) {
      dispatch(
        fetchMatchmakingResults({
          id: activeListingId,
          statusFilter: null,
        })
      );
    }
  }, [activeData, activeCounterparty, open, activeListingId, dispatch]);

  const getFinalizedInjectionData = () => {
    if (finalTermsData?.slotInjects?.length > 0) {
      return finalTermsData.slotInjects;
    }

    const finalizedData =
      activeData?.finalizedConnectionValues ||
      activeCounterparty?.finalizedConnectionValues;
    if (finalizedData?.slotInjects?.length > 0) {
      return finalizedData.slotInjects;
    }

    return selfListingData?.bankingSlotInjects || [];
  };

  const getFinalizedDrawalData = () => {
    if (finalTermsData?.slotDrawls?.length > 0) {
      return finalTermsData.slotDrawls;
    }

    const finalizedData =
      activeData?.finalizedConnectionValues ||
      activeCounterparty?.finalizedConnectionValues;
    if (finalizedData?.slotDrawls?.length > 0) {
      return finalizedData.slotDrawls;
    }

    return selfListingData?.bankingSlotDrawls || [];
  };

  const renderMultipleSlots = (slots, isInjection = true) => {
    if (!slots || slots.length === 0) return "—";

    const slot = slots[0];
    const displayEndTime = (endTime) => {
      if (!endTime) return "—";
      if (endTime === "23:59" || endTime === "00:00") return "24:00";
      return endTime;
    };

    const displayStartTime = (startTime) => {
      if (!startTime) return "—";
      return startTime;
    };

    return (
      <>
        {slot.dateStartAt || formatDate(slot.startDate) || "—"} to{" "}
        {slot.dateEndAt || formatDate(slot.endDate) || "—"}
        <br />
        {displayStartTime(slot.slotStartAt || slot.startTime)} to{" "}
        {displayEndTime(slot.slotEndAt || slot.endTime)}
      </>
    );
  };

  const connectLoading = useSelector(
    (state) => state.notificationSlice?.loading || false
  );

  const bothPartiesActionMode =
    !viewOnly &&
    showButtons &&
    !showAlreadyAcceptedMessage &&
    !isApprovalMode &&
    selfListingData?.status !== "TERMS_ACCEPTED" &&
    activeCounterparty?.status !== "TERMS_ACCEPTED";

  useEffect(() => {
    if (bothPartiesActionMode && activeListingId) {
      dispatch(
        fetchMatchmakingResults({
          id: activeListingId,
          statusFilter: null,
        })
      );

      dispatch(fetchAllBankingListings());

      dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
    }
  }, [bothPartiesActionMode, activeListingId, dispatch]);

  const isTermsAccepted =
    activeCounterparty?.status === "TERMS_ACCEPTED" ||
    activeCounterparty?.status === "COMPLETED";

  return (
    <>
      {editFinalModal && (
        <FinalEditModal
          open={editFinalModal}
          handleClose={handleCloseEditFinalModal}
          connectionId={activeCounterparty?.id}
          selfListingData={selfListingData}
          contractId={contractId}
          onSubmit={handleFinalSubmit}
          finalModalEditData={finalModalEditData}
          handleInjectionChange={(idx, field, val) => {
            // Normalize Date objects to YYYY-MM-DD strings so we never store raw Date instances
            const normalizeDate = (v) => {
              if (v instanceof Date) return v.toISOString().slice(0, 10);
              return v;
            };
            const updatedRows = [...finalModalEditData.slotInjects];
            const newVal =
              field === "dateStartAt" || field === "dateEndAt"
                ? normalizeDate(val)
                : val;
            updatedRows[idx][field] = newVal;
            setFinalModalEditData((prev) => ({
              ...prev,
              slotInjects: updatedRows,
            }));
          }}
          handleDrawalChange={(idx, field, val) => {
            const normalizeDate = (v) => {
              if (v instanceof Date) return v.toISOString().slice(0, 10);
              return v;
            };
            const updatedRows = [...finalModalEditData.slotDrawls];
            const newVal =
              field === "dateStartAt" || field === "dateEndAt"
                ? normalizeDate(val)
                : val;
            updatedRows[idx][field] = newVal;
            setFinalModalEditData((prev) => ({
              ...prev,
              slotDrawls: updatedRows,
            }));
          }}
          addInjectionRow={() =>
            setFinalModalEditData((prev) => ({
              ...prev,
              slotInjects: [
                ...prev.slotInjects,
                {
                  dateStartAt: "",
                  dateEndAt: "",
                  slotStartAt: "",
                  slotEndAt: "",
                  requiredQty: "",
                },
              ],
            }))
          }
          removeInjectionRow={(idx) =>
            setFinalModalEditData((prev) => ({
              ...prev,
              slotInjects: prev.slotInjects.filter((_, i) => i !== idx),
            }))
          }
          addDrawalRow={() =>
            setFinalModalEditData((prev) => ({
              ...prev,
              slotDrawls: [
                ...prev.slotDrawls,
                {
                  dateStartAt: "",
                  dateEndAt: "",
                  slotStartAt: "",
                  slotEndAt: "",
                  requiredQty: "",
                },
              ],
            }))
          }
          removeDrawalRow={(idx) =>
            setFinalModalEditData((prev) => ({
              ...prev,
              slotDrawls: prev.slotDrawls.filter((_, i) => i !== idx),
            }))
          }
        />
      )}

      {viewInjection.open && (
        <ViewInjectionModal
          open={viewInjection.open}
          handleClose={() => setViewInjection({ open: false, data: [] })}
          data={viewInjection.data}
        />
      )}

      {viewDrawal.open && (
        <ViewDrawalModal
          open={viewDrawal.open}
          handleClose={() => setViewDrawal({ open: false, data: [] })}
          data={viewDrawal.data}
        />
      )}

      <ConnectionAcceptModal
        open={confirmPopupOpen}
        handleClose={() => setConfirmPopupOpen(false)}
        onConfirm={() => {
          handleAcceptClick(popupAction);
          setConfirmPopupOpen(false);
        }}
        content={
          popupAction === "ACCEPTED"
            ? "accept"
            : popupAction === "DECLINED"
            ? "decline"
            : popupAction === "FINALIZE"
            ? "finalize"
            : popupAction === "CLOSE"
            ? "close"
            : ""
        }
        actionType={popupAction}
        insideTermsModal={true}
        loading={connectLoading}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.headingText}`}
          >
            <div className={Styles.headingTitleGroup}>
              <div className={Styles.headingText}>Terms and Conditions</div>
              {contractId && (
                <span className={Styles.contractChip}>
                  <span className={Styles.chipLabel}>Contract ID: </span>
                  <span className={Styles.contractValue} title={contractId}>
                    {contractId}
                  </span>
                </span>
              )}
            </div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
          <div className={Styles.termsMainContainer}>
            <div className={Styles.termsTableSection}>
              <div className={Styles.termsTableContent}>
                <div className={Styles.tableTermsIcon}>Category</div>
                <div>My Listing</div>
                <div>Counterparty Listing</div>
                <div className={Styles.tableFinalIcon}>
                  {isTermsAccepted ? "Finalized T&C" : "Draft T&C"}{" "}
                  {!viewOnly && (
                    <span
                      className={Styles.iconSpanTerms}
                      onClick={handleEditFinalModal}
                    >
                      <EditIcon
                        style={{ width: 22, height: 22, cursor: "pointer" }}
                      />
                    </span>
                  )}
                </div>
              </div>

              <div className={Styles.termsTableContainer}>
                <>
                  {renderTermData(
                    "Name",
                    <span style={{ color: "#6F6D7C", fontWeight: "500" }}>
                      {currentUserCompanyName}
                    </span>,
                    <span style={{ color: "var(--primary-color-text)", fontWeight: "500" }}>
                      {activeCounterparty?.companyName || ""}
                    </span>,
                    <span style={{ color: "#34656D", fontWeight: "600" }}>
                      {finalTermsData?.companyName ||
                        `${currentUserCompanyName}`}
                    </span>
                  )}

                  {renderTermData(
                    "Banking Factor",
                    <span style={{ color: "#525062", fontWeight: "400" }}>
                      {`${selfListingData?.bankingFactor || "—"} %`}
                    </span>,
                    <span style={{ color: "var(--primary-color-text)", fontWeight: "400" }}>
                      {`${activeCounterparty?.bankingFactor || "—"} %`}
                    </span>,
                    <span
                      style={{
                        color: isValueModified(
                          selfListingData?.bankingFactor,
                          finalTermsData?.bankingFactor
                        )
                          ? "#34656D"
                          : "#6F6D7C",
                        fontWeight: isValueModified(
                          selfListingData?.bankingFactor,
                          finalTermsData?.bankingFactor
                        )
                          ? 600
                          : 400,
                      }}
                    >
                      {`${finalTermsData?.bankingFactor} %`}
                    </span>
                  )}

                  {renderTermData(
                    "Settlement Rate",
                    <span style={{ color: "#525062", fontWeight: "400" }}>
                      {`${selfListingData?.settlementRate || "—"} ₹/kWh`}
                    </span>,
                    <span style={{ color: "var(--primary-color-text)", fontWeight: "400" }}>
                      {`${activeCounterparty?.settlementRate || "—"} ₹/kWh`}
                    </span>,
                    <span
                      style={{
                        color: isValueModified(
                          selfListingData?.settlementRate,
                          finalTermsData?.settlementRate
                        )
                          ? "#34656D"
                          : "#6F6D7C",
                        fontWeight: isValueModified(
                          selfListingData?.settlementRate,
                          finalTermsData?.settlementRate
                        )
                          ? 600
                          : 400,
                      }}
                    >
                      {`${finalTermsData?.settlementRate} ${
                        finalTermsData?.settlementRate.includes("%")
                          ? ""
                          : "₹/kWh"
                      }`}
                    </span>
                  )}

                  {renderTermData(
                    "Injection Quantity",
                    <span style={{ color: "#525062", fontWeight: "400" }}>
                      {`${
                        selfListingData?.bankingSlotInjects?.[0]?.requiredQty ||
                        "—"
                      } (MW)`}
                    </span>,
                    <span style={{ color: "var(--primary-color-text)", fontWeight: "400" }}>
                      {`${
                        activeCounterparty?.bankingSlotInjects?.[0]
                          ?.requiredQty || "—"
                      } (MW)`}
                    </span>,
                    <span
                      style={{
                        color: isValueModified(
                          selfListingData?.bankingSlotInjects?.[0]?.requiredQty,
                          finalTermsData?.slotInjects?.[0]?.requiredQty
                        )
                          ? "#34656D"
                          : "#6F6D7C",
                        fontWeight: isValueModified(
                          selfListingData?.bankingSlotInjects?.[0]?.requiredQty,
                          finalTermsData?.slotInjects?.[0]?.requiredQty
                        )
                          ? 600
                          : 400,
                      }}
                    >
                      {`${
                        finalTermsData?.slotInjects?.[0]?.requiredQty ??
                        selfListingData?.bankingSlotInjects?.[0]?.requiredQty ??
                        "—"
                      } (MW)`}
                    </span>
                  )}

                  {renderTermData(
                    "Drawal Quantity",
                    <span style={{ color: "#525062", fontWeight: "400" }}>
                      {`${
                        selfListingData?.bankingSlotDrawls?.[0]?.requiredQty ||
                        "—"
                      } (MW)`}
                    </span>,
                    <span style={{ color: "var(--primary-color-text)", fontWeight: "400" }}>
                      {`${
                        activeCounterparty?.bankingSlotDrawls?.[0]
                          ?.requiredQty || "—"
                      } (MW)`}
                    </span>,
                    <span
                      style={{
                        color: isValueModified(
                          selfListingData?.bankingSlotDrawls?.[0]?.requiredQty,
                          finalTermsData?.slotDrawls?.[0]?.requiredQty
                        )
                          ? "#34656D"
                          : "#6F6D7C",
                        fontWeight: isValueModified(
                          selfListingData?.bankingSlotDrawls?.[0]?.requiredQty,
                          finalTermsData?.slotDrawls?.[0]?.requiredQty
                        )
                          ? 600
                          : 400,
                      }}
                    >
                      {`${
                        finalTermsData?.slotDrawls?.[0]?.requiredQty ??
                        selfListingData?.bankingSlotDrawls?.[0]?.requiredQty ??
                        "—"
                      } (MW)`}
                    </span>
                  )}
                  {renderTermData(
                    "Injection Date & Time Range",
                    <>
                      {renderMultipleSlots(
                        selfListingData?.bankingSlotInjects,
                        true
                      )}
                      <SquareIcon
                        style={{
                          paddingBottom: "16px",
                          paddingLeft: "12.5px",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "20px",
                          height: "20px",
                        }}
                        onClick={() =>
                          setViewInjection({
                            open: true,
                            data: selfListingData?.bankingSlotInjects || [],
                          })
                        }
                      />
                    </>,
                    <>
                      {renderMultipleSlots(
                        activeCounterparty?.bankingSlotInjects,
                        true
                      )}
                      <SquareIcon
                        style={{
                          paddingBottom: "16px",
                          paddingLeft: "11.5px",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "20px",
                          height: "20px",
                        }}
                        onClick={() =>
                          setViewInjection({
                            open: true,
                            data: activeCounterparty?.bankingSlotInjects || [],
                          })
                        }
                      />
                    </>,
                    <>
                      {(() => {
                        const finalizedInjections = getFinalizedInjectionData();
                        return (
                          <>
                            {renderMultipleSlots(finalizedInjections, true)}
                            <SquareIcon
                              style={{
                                paddingBottom: "16px",
                                paddingLeft: "13px",
                                cursor: "pointer",
                                fontSize: "12px",
                                width: "20px",
                                height: "20px",
                              }}
                              onClick={() => {
                                if (finalTermsData?.slotInjects?.length > 0) {
                                  setViewInjection({
                                    open: true,
                                    data: finalTermsData.slotInjects,
                                  });
                                } else {
                                  const finalizedData =
                                    activeData?.finalizedConnectionValues ||
                                    activeCounterparty?.finalizedConnectionValues;

                                  const transformedFinalizedData =
                                    finalizedData?.slotInjects?.map((slot) => ({
                                      ...slot,
                                      slotEndAt:
                                        slot.slotEndAt === "00:00"
                                          ? "24:00"
                                          : slot.slotEndAt,
                                    })) || [];

                                  setViewInjection({
                                    open: true,
                                    data:
                                      transformedFinalizedData.length > 0
                                        ? transformedFinalizedData
                                        : (
                                            selfListingData?.bankingSlotInjects ||
                                            []
                                          ).map((slot) => ({
                                            ...slot,
                                            slotEndAt:
                                              slot.slotEndAt === "00:00"
                                                ? "24:00"
                                                : slot.slotEndAt,
                                          })),
                                  });
                                }
                              }}
                            />
                          </>
                        );
                      })()}
                    </>
                  )}

                  {renderTermData(
                    "Drawal Date & Time Range",
                    <>
                      {renderMultipleSlots(
                        selfListingData?.bankingSlotDrawls,
                        false
                      )}
                      <SquareIcon
                        style={{
                          paddingBottom: "16px",
                          paddingLeft: "8px",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "20px",
                          height: "20px",
                        }}
                        onClick={() =>
                          setViewDrawal({
                            open: true,
                            data: selfListingData?.bankingSlotDrawls || [],
                          })
                        }
                      />
                    </>,
                    <>
                      {renderMultipleSlots(
                        activeCounterparty?.bankingSlotDrawls,
                        false
                      )}
                      <SquareIcon
                        style={{
                          paddingBottom: "16px",
                          paddingLeft: "8px",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "20px",
                          height: "20px",
                        }}
                        onClick={() =>
                          setViewDrawal({
                            open: true,
                            data: activeCounterparty?.bankingSlotDrawls || [],
                          })
                        }
                      />
                    </>,
                    <>
                      {(() => {
                        const finalizedDrawals = getFinalizedDrawalData();
                        return (
                          <>
                            {renderMultipleSlots(finalizedDrawals, false)}
                            <SquareIcon
                              style={{
                                paddingBottom: "16px",
                                paddingLeft: "8px",
                                cursor: "pointer",
                                fontSize: "12px",
                                width: "20px",
                                height: "20px",
                              }}
                              onClick={() => {
                                if (finalTermsData?.slotDrawls?.length > 0) {
                                  setViewDrawal({
                                    open: true,
                                    data: finalTermsData.slotDrawls,
                                  });
                                } else {
                                  const finalizedData =
                                    activeData?.finalizedConnectionValues ||
                                    activeCounterparty?.finalizedConnectionValues;

                                  const transformedFinalizedData =
                                    finalizedData?.slotDrawls?.map((slot) => ({
                                      ...slot,
                                      slotEndAt:
                                        slot.slotEndAt === "00:00"
                                          ? "24:00"
                                          : slot.slotEndAt,
                                    })) || [];

                                  setViewDrawal({
                                    open: true,
                                    data:
                                      transformedFinalizedData.length > 0
                                        ? transformedFinalizedData
                                        : (
                                            selfListingData?.bankingSlotDrawls ||
                                            []
                                          ).map((slot) => ({
                                            ...slot,
                                            slotEndAt:
                                              slot.slotEndAt === "00:00"
                                                ? "24:00"
                                                : slot.slotEndAt,
                                          })),
                                  });
                                }
                              }}
                            />
                          </>
                        );
                      })()}
                    </>,

                    Styles.drawalInjectRows
                  )}

                  {viewMore && (
                    <>
                      {renderTermData(
                        "Type",
                        <span style={{ color: "#525062", fontWeight: "400" }}>
                          {selfListingData?.gnaepleListing || "—"}
                        </span>,
                        <span style={{ color: "var(--primary-color-text)", fontWeight: "400" }}>
                          {activeCounterparty?.gnaepleListing || "—"}
                        </span>,
                        <span
                          style={{
                            color: isValueModified(
                              selfListingData?.gnaepleListing,
                              finalTermsData?.activeType
                            )
                              ? "#34656D"
                              : "#6F6D7C",
                            fontWeight: isValueModified(
                              selfListingData?.gnaepleListing,
                              finalTermsData?.activeType
                            )
                              ? 600
                              : 400,
                          }}
                        >
                          {finalTermsData?.activeType ||
                            selfListingData?.gnaepleListing ||
                            "—"}
                        </span>
                      )}
                      {renderTermData(
                        "Listing Expiry Date and Time",
                        selfListingData?.expiryAt || "—",
                        activeCounterparty?.expiryAt || "—",
                        selfListingData?.expiryAt || "—"
                      )}
                      {renderTermData(
                        "Terms Expiry Date and Time",
                        formatDateTime(selfListingData?.termsExpiryAt),
                        formatDateTime(activeCounterparty?.termsExpiryAt),
                        formatDateTime(selfListingData?.termsExpiryAt)
                      )}
                    </>
                  )}

                  <div
                    onClick={toggleViewMore}
                    className={`display_flex_row_center ${Styles.viewMoreBtn}`}
                  >
                    {viewMore ? "View Less" : "View More"}
                  </div>
                </>
              </div>
            </div>
            {(isApprovalMode ||
              (!viewOnly && showButtons && !showAlreadyAcceptedMessage)) && (
              <div className={Styles.termsbuttonSection}>
                {bothPartiesActionMode ? (
                  <>
                    <div
                      className={Styles.cancelBankButton}
                      onClick={() => {
                        handleClose();
                      }}
                    >
                      Close
                    </div>
                    <div
                      className={`${Styles.acceptBtn}${
                        !finalTermsData ? ` ${Styles.disabledBtn}` : ""
                      }`}
                      onClick={() => {
                        if (finalTermsData) {
                          openConfirmPopup("FINALIZE");
                        }
                      }}
                    >
                      Submit
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={Styles.declinesBtn}
                      onClick={() => {
                        if (isApprovalMode) {
                          openConfirmPopup("DECLINED");
                        } else {
                          handleClose();
                        }
                      }}
                    >
                      Decline
                    </div>
                    <div
                      className={`${Styles.acceptBtn}${
                        !finalTermsData ? ` ${Styles.disabledBtn}` : ""
                      }`}
                      onClick={() => {
                        if (finalTermsData) {
                          openConfirmPopup("ACCEPTED");
                        }
                      }}
                    >
                      Accept
                    </div>
                  </>
                )}
              </div>
            )}

            {/* {!viewOnly &&
              !isApprovalMode &&
              hasAnyCounterpartyAcceptedTerms() &&
              activeCounterparty?.status !== "ACTIVE" && (
                <div className={Styles.termsbuttonSection}>
                  <div
                    style={{
                      textAlign: "center",
                      color: "#666",
                      fontSize: "14px",
                      padding: "10px 0",
                    }}
                  >
                    Terms and conditions have already been accepted by another
                    counterparty.
                  </div>
                </div>
              )} */}
          </div>

          {isLoading && (
            <div className={Styles.loaderOverlay}>
              <Loader />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default TermsConditionModal;
